import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, Check, MapPin, Moon, X as XIcon } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MaskLines, Reveal } from "@/components/site/Motion";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { useEnquiry } from "@/context/EnquiryContext";
import { T } from "@/context/ContentContext";
import { useSeo } from "@/hooks/useSeo";
import { api, inr } from "@/lib/api";

function TripHero({ trip }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  return (
    <section ref={ref} className="relative h-[78svh] min-h-[520px] overflow-hidden bg-ink" data-testid="trip-detail-hero">
      <motion.img style={{ y }} initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
        src={trip.image} alt={trip.title} className="absolute inset-0 h-[120%] w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/35 to-ink/20" />
      <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-12 text-cream">
        <Link to="/trips" data-testid="trip-back-link" className="inline-flex items-center gap-2 eyebrow text-cream/80 hover:text-cream mb-6"><ArrowLeft className="h-4 w-4" /> <T k="trip.back">All trips</T></Link>
        <p className="eyebrow text-gold mb-4">{trip.destination}{trip.badge ? ` · ${trip.badge}` : ""}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.035em] leading-[0.98] max-w-4xl" data-testid="trip-detail-title">
          <MaskLines lines={[trip.title]} />
        </h1>
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-cream/85 font-mono">
          <span className="inline-flex items-center gap-2"><Moon className="h-4 w-4" />{trip.nights}N / {trip.days}D</span>
          <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4" />{trip.start_location} – {trip.end_location}</span>
        </div>
      </div>
    </section>
  );
}

function List({ items, icon: Icon, tone }) {
  return (
    <ul className="space-y-3">
      {(items || []).map((x) => (
        <li key={x} className="flex gap-3"><Icon className={`h-5 w-5 shrink-0 mt-0.5 ${tone}`} />{x}</li>
      ))}
    </ul>
  );
}

function BookingCard({ trip }) {
  const [date, setDate] = useState(trip.dates?.[0] || "");
  return (
    <div className="rounded-[28px] bg-paper border border-ink/10 p-6 sm:p-8 shadow-[0_30px_80px_-40px_rgba(21,17,14,0.35)]" data-testid="trip-booking-card">
      {trip.original_price > trip.price && <p className="font-mono text-sm line-through text-stone-600">{inr(trip.original_price)}</p>}
      <p className="font-display text-4xl font-extrabold" data-testid="trip-detail-price">{inr(trip.price)}<span className="text-sm font-body font-medium text-stone-600"> /person onwards</span></p>
      {trip.dates?.length > 0 && (
        <div className="mt-6">
          <T k="trip.departures" as="p" className="eyebrow text-stone-600 mb-3">Departures</T>
          <div className="flex flex-wrap gap-2">
            {trip.dates.map((d) => (
              <button key={d} onClick={() => setDate(d)} data-testid={`trip-date-${d.replace(/\s/g, "-")}`}
                className={`rounded-full px-3 py-1.5 text-sm font-mono border transition-colors ${date === d ? "bg-terra text-paper border-terra" : "border-ink/15 hover:border-ink/40"}`}>{d}</button>
            ))}
          </div>
        </div>
      )}
      <div className="mt-6">
        <EnquiryForm key={date} idPrefix="trip-detail" preset={{ destination: trip.destination, trip_slug: trip.slug, trip_title: `${trip.title}${date ? ` (${date})` : ""}` }} />
      </div>
    </div>
  );
}

export default function TripDetail() {
  const { slug } = useParams();
  const { openEnquiry } = useEnquiry();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(false);
  useSeo({
    title: trip ? `${trip.title} | ${trip.destination} Tour Package` : "",
    description: trip ? `${trip.title}: ${trip.days ? `${trip.days}-day ` : ""}${trip.destination} trip by Trippy Tastic, Delhi. Itinerary, inclusions and departures. Enquire for a callback.` : "",
    path: `/trips/${slug}`,
  });

  useEffect(() => {
    setTrip(null);
    setError(false);
    api.get(`/trips/${slug}`).then((r) => setTrip(r.data)).catch(() => setError(true));
  }, [slug]);

  if (error) return (
    <main className="min-h-screen grid place-items-center bg-paper px-6 text-center" data-testid="trip-not-found">
      <div><p className="font-serif italic text-4xl">This trip wandered off.</p><Link to="/trips" className="mt-6 inline-block underline">See all trips</Link></div>
    </main>
  );
  if (!trip) return <main className="min-h-screen bg-ink" />;

  return (
    <main className="bg-paper" data-testid="trip-detail-page">
      <TripHero trip={trip} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7 space-y-16">
          <Reveal>
            <T k="trip.overview" as="p" className="eyebrow text-terra mb-4">Overview</T>
            <p className="text-lg sm:text-xl leading-relaxed text-ink/85">{trip.overview}</p>
            {trip.highlights?.length > 0 && (
              <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                {trip.highlights.map((h) => <li key={h} className="flex gap-3 rounded-2xl bg-cream px-4 py-3"><Check className="h-5 w-5 text-terra shrink-0" />{h}</li>)}
              </ul>
            )}
          </Reveal>
          {trip.gallery?.length > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:gap-4" data-testid="trip-gallery">
              {trip.gallery.slice(0, 4).map((g, i) => (
                <Reveal key={g} delay={i * 0.08} className={`overflow-hidden ${i === 0 ? "arch-sm col-span-2 aspect-[16/9]" : "rounded-[20px] aspect-[4/3]"} ${trip.gallery.length === 2 && i === 1 ? "col-span-2" : ""}`}>
                  <img src={g} alt={`${trip.destination} ${i + 1}`} loading="lazy" className="h-full w-full object-cover hover:scale-105 transition-transform duration-1000" />
                </Reveal>
              ))}
            </div>
          )}
          <Reveal>
            <T k="trip.days" as="p" className="eyebrow text-terra mb-4">Day by day</T>
            <Accordion type="single" collapsible defaultValue="day-1" data-testid="trip-itinerary">
              {(trip.itinerary || []).map((d) => (
                <AccordionItem key={d.day} value={`day-${d.day}`} className="border-ink/10">
                  <AccordionTrigger data-testid={`itinerary-day-${d.day}`} className="py-5 hover:no-underline text-left">
                    <span className="flex items-baseline gap-4"><span className="font-mono text-xs text-terra">DAY {String(d.day).padStart(2, "0")}</span><span className="font-display text-lg sm:text-xl font-bold">{d.title}</span></span>
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-ink/80 pl-0 sm:pl-[4.5rem]">{d.description}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal>
            <Tabs defaultValue="inc">
              <TabsList className="rounded-full bg-cream p-1 h-auto">
                <TabsTrigger value="inc" data-testid="tab-inclusions" className="rounded-full px-5 py-2 data-[state=active]:bg-ink data-[state=active]:text-cream">Inclusions</TabsTrigger>
                <TabsTrigger value="exc" data-testid="tab-exclusions" className="rounded-full px-5 py-2 data-[state=active]:bg-ink data-[state=active]:text-cream">Exclusions</TabsTrigger>
              </TabsList>
              <TabsContent value="inc" className="mt-6"><List items={trip.inclusions} icon={Check} tone="text-emerald-700" /></TabsContent>
              <TabsContent value="exc" className="mt-6"><List items={trip.exclusions} icon={XIcon} tone="text-terra" /></TabsContent>
            </Tabs>
          </Reveal>
        </div>
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-28"><BookingCard trip={trip} /></div>
        </aside>
      </div>
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-paper/95 backdrop-blur-xl border-t border-ink/10 px-5 py-3 flex items-center justify-between gap-3">
        <p className="font-display text-xl font-extrabold">{inr(trip.price)}</p>
        <button onClick={() => openEnquiry({ destination: trip.destination, trip_slug: trip.slug, trip_title: trip.title })} data-testid="trip-detail-enquire-btn"
          className="rounded-full bg-terra text-paper px-5 py-2.5 font-semibold">Enquire</button>
      </div>
    </main>
  );
}
